import { css, unsafeCSS } from 'lit'
import tokens from '@lansforsakringar/core-tokens'
import { vars, scoped, declare, override } from '#utils/styles'

export const locals = scoped('dialog-sheet', [
	'background_color',
	'width',
	'height'
] as const)

/**
 * A landscape phone can easily exceed the 600px width breakpoint while
 * still being short, so "desktop layout" also requires enough height —
 * otherwise a phone in landscape would incorrectly get the side-sheet
 * layout meant for tablets/desktop. Keep in sync with the equivalent
 * constants in lfds-sheet.ts.
 */
const desktop = unsafeCSS('(min-width: 600px) and (min-height: 600px)')
const mobile = unsafeCSS('(max-width: 599px), (max-height: 599px)')

export default css`
	:host {
		${declare(locals, {
			background_color: tokens.color_background_primary,
			height: '85svh',
			width: '31rem'
		})};
		display: contents;
	}

	:host([migration~="light-background"]) {
		${override(locals.background_color, tokens.color_background_secondary)}
	}

	.dialog {
		display: grid;
		box-sizing: border-box;
		background-color: ${vars(locals.background_color)};
		position: fixed;
		inset-inline-end: 0;
		inset-block-end: 0;
		max-width: unset;
		max-height: unset;
		border: none;
		inset-inline-start: unset;
		inset-block-start: unset;
		padding: 0;
		z-index: 1;
		transform: translateY(100%);
		@media (prefers-reduced-motion: no-preference) {
		  transition:
				transform 300ms var(--ease-in),
				height 300ms var(--ease-in),
				visibility 0ms 300ms;
		}
		overflow: hidden;
		container-type: size;
		height: ${vars(locals.height)};
		width: 100svw;
		margin: unset;
		visibility: hidden;
		border-start-start-radius: ${vars(tokens.border_radius_lg)};
		border-start-end-radius: ${vars(tokens.border_radius_lg)};

		box-shadow: 0 0 24px 0 rgba(0, 0, 0, 0.25);
	}

	.dialog.hidden {
		width: 0;
	}

	.dialog.open {
		visibility: visible;
		transform: translateY(0);
		@media (prefers-reduced-motion: no-preference) {
  		transition:
  			transform 300ms var(--ease-out),
  			height 300ms var(--ease-out),
  			visibility 0ms 0ms;
		}
	}

	.dialog.resizing {
		transition: none;
	}

	@media ${desktop} {
		.dialog {
			height: 100%;
			width: min(
				${vars(locals.width)},
				calc(100% - ${vars(tokens.spacing_2xl)})
			);
			transform: translateX(100%);
			inset-block-start: 0;
			inset-block-end: 0;
			border-radius: 0;
		}

		.dialog.open {
			transform: translateX(0);
		}
	}

	.dialog::backdrop {
		opacity: 0;
	}

	.dialog.backdrop::backdrop {
		opacity: 0;
		background-color: ${vars(tokens.color_background_transparent_secondary)};
		transition: opacity 300ms var(--ease-in);
	}

	.dialog.backdrop.open::backdrop {
		opacity: 1;
		transition-timing-function: var(--ease-out);
	}

	.size-small {
		${override(locals.width, '31rem')};
	}

	.size-medium {
		${override(locals.width, '46rem')};
	}

	.size-large {
		${override(locals.width, '61rem')};
	}

	/** @deprecated Use height-hug */
	.height-small {
		${override(locals.height, '50svh')};
	}

	/** @deprecated Use height-hug or height-fill */
	.height-medium {
		${override(
			locals.height,
			`calc(100% - calc(${vars(tokens.spacing_3xl)} * 2))`
		)};
	}

	.height-hug,
	.dialog.resizable {
		max-height: none;
		container-type: normal;

		@media ${desktop} {
			height: 100%;
			max-height: unset;
			container-type: size;
		}
	}

	.height-fill {
		${override(
			locals.height,
			`calc(100% - calc(${vars(tokens.spacing_3xl)} * 2))`
		)};
	}

	.inner {
		display: grid;
		grid-template-rows: auto 1fr auto;
		grid-template-columns: 1fr;
		box-sizing: border-box;
		overflow: hidden;
		height: 100%;
		background-color: ${vars(locals.background_color)};

		@media ${desktop} {
			grid-template-rows: auto auto 1fr auto;
		}

		&.sticky .header-wrapper {
			border-block-end-color: ${vars(tokens.color_border_secondary)};
		}
	}

	.header-wrapper {
		grid-row: 1;
		grid-column: 1;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		box-sizing: border-box;
		padding: ${vars(tokens.spacing_lg)};
		padding-top: calc(${vars(tokens.spacing_lg)} + ${vars(tokens.spacing_xs)});
		padding-block-end: ${vars(tokens.spacing_sm)};
		padding-inline: 4rem;
		background-color: inherit;
		border-block-end: ${vars(tokens.border_width_default)} solid transparent;
		transition: border-color 125ms var(--ease-out);
		z-index: 1;
		touch-action: none;

		/* drag handle (mobile) */
		&::before {
			content: '';
			position: absolute;
			top: ${vars(tokens.spacing_sm)};
			left: 50%;
			translate: -50% 0;
			width: ${vars(tokens.spacing_2xl)};
			height: ${vars(tokens.spacing_xs)};
			background: ${vars(tokens.color_icon_secondary)};
			border-radius: ${vars(tokens.border_radius_sm)};
			pointer-events: none;
			z-index: 3;
		}

		@media (hover: hover), ${desktop} {
			&::before {
				content: unset;
			}
		}

		@media ${desktop} {
			grid-row: 2;
			justify-content: flex-start;
			padding-inline: ${vars(tokens.spacing_lg)};
			gap: ${vars(tokens.spacing_sm)};
			align-items: start;
			padding-top: ${vars(tokens.spacing_lg)};
			touch-action: auto;
		}
	}

	.header-actions {
		grid-row: 1;
		grid-column: 1;
		display: flex;
		justify-content: space-between;
		align-items: flex-start;
		box-sizing: border-box;
		padding: ${vars(tokens.spacing_lg)};
		padding-block-end: ${vars(tokens.spacing_sm)};
		z-index: 2;
		pointer-events: none;

		@media ${desktop} {
			grid-row: 1;
			padding: ${vars(tokens.spacing_lg)} ${vars(tokens.spacing_lg)} 0;
		}
	}

	.header-action {
  	font-family: ${vars(tokens.font_family_label)};
  	font-size: ${vars(tokens.text_size_label_default)};
  	font-weight: ${vars(tokens.font_weight_regular)};
 		color: ${vars(tokens.color_text_link)};
    line-height: ${vars(tokens.line_height_label_default)};
  	text-wrap: pretty;
  	text-decoration: none;
  	appearance: none;
  	border: 0;
    border-radius: ${vars(tokens.border_radius_round)};
  	padding: 0;
  	background: transparent;
  	user-select: none;
  	cursor: pointer;
  	pointer-events: auto;
    position: relative;

		&::after {
       content: '';
       position: absolute;
       left: -4px;
       top: -4px;
       right: -4px;
       bottom: -4px;
		}

		&:focus {
			outline: 0;
		}

		&:hover,
	  &:active {
			color: ${vars(tokens.color_text_link_hover)};
		}
	}

	.header-action--end {
	  margin-inline-start: auto;
	}

	/*
	 * Text-only action buttons (e.g. "Avbryt") have no icon, so they need
	 * an explicit min-height to match the icon button size (24px icon + padding).
	 * This keeps the hit area consistent — without it, the ::after pseudo-element
	 * (which extends the tap target) would overshoot visually.
	 */
	.header-action--text-only {
		min-height: calc(${vars(tokens.size_control_sm)} - ${vars(tokens.spacing_sm)}); \* [1] */
		display: flex;
		align-items: center;
	}

	.header-action--start {
	  margin-inline-end: auto;
	}

	.header-action__label {
		gap: ${vars(tokens.spacing_sm)};
		align-items: center;
		display: flex;
		width: fit-content;
		border-radius: ${vars(tokens.border_radius_xs)};

		@media ${desktop} {
		  padding-inline-start: ${vars(tokens.spacing_sm)};
		}
	}

	.header-action.focus-visible:focus-visible .header-action__label {
		outline: var(--focus-outline);
		border-radius: ${vars(tokens.border_radius_round)};
	}

  .header-action__label-text {
		display: none;

		@media ${desktop} {
		  display: inline;
		}
	}

	.header-action--text-only .header-action__label-text {
		display: inline;
	}

	.header-action__label-icon {
	  border-radius: ${vars(tokens.border_radius_round)};
		background-color: ${vars(tokens.component_button_icon_button_background_color)};
		padding: ${vars(tokens.spacing_sm)};
	}

	.header-action:hover .header-action__label-icon {
		background: color-mix(in srgb, ${vars(tokens.color_state_layer_on_light)} calc(${vars(tokens.effect_opacity_state_hover)} * 100%), ${vars(tokens.component_button_icon_button_background_color)});
	}

	.header-action:focus .header-action__label-icon {
		background: color-mix(in srgb, ${vars(tokens.color_state_layer_on_light)} calc(${vars(tokens.effect_opacity_state_focused)} * 100%), ${vars(tokens.component_button_icon_button_background_color)});
	}

	.header-action:active .header-action__label-icon {
		background: color-mix(in srgb, ${vars(tokens.color_state_layer_on_light)} calc(${vars(tokens.effect_opacity_state_pressed)} * 100%), ${vars(tokens.component_button_icon_button_background_color)});
	}

	.heading {
		font-family: ${vars(tokens.font_family_heading)};
		font-size: ${vars(tokens.text_size_heading_level_3)};
		font-weight: ${vars(tokens.font_weight_bold)};
		letter-spacing: 0;
		line-height: ${vars(tokens.line_height_heading_level_3)};
		text-wrap: balance;
		text-align: center;
		margin-block: 0;
		color: ${vars(tokens.color_text_brand_primary)};
		overflow-wrap: anywhere;

		&:focus {
			outline: 0;
		}

		&.focus-visible:focus-visible {
			outline: var(--focus-outline);
			outline-offset: -4px;
		}

		@media ${desktop} {
			font-size: ${vars(tokens.text_size_heading_level_2)};
			line-height: ${vars(tokens.line_height_heading_level_2)};
			text-align: left;
			overflow-wrap: break-word;
		}
	}

	.byline {
	  font-weight: ${vars(tokens.font_weight_regular)};
	  font-size: ${vars(tokens.text_size_body_default)};
		color: ${vars(tokens.color_text_secondary)};
		line-height: ${vars(tokens.line_height_body_default)};

		@media ${desktop} {
		  margin-top: ${vars(tokens.spacing_md)};
		}
	}

	.body {
		grid-row: 2;
		overflow: auto;
		scroll-behavior: smooth;
		min-height: 0;
		padding: ${vars(tokens.spacing_md)};
		padding-block-start: ${vars(tokens.spacing_sm)};
		box-sizing: border-box;
		overscroll-behavior-y: none;

		&.snap-hug {
			@media ${mobile} {
				overflow: hidden;
			}
		}

		@media ${desktop} {
			grid-row: 3;
			padding: ${vars(tokens.spacing_lg)};
			padding-block-start: ${vars(tokens.spacing_sm)};
		}
	}

	:host([migration~="remove-padding"]) .body {
		padding: ${vars(tokens.spacing_md)} 0;

		@media ${desktop} {
			padding: ${vars(tokens.spacing_lg)} 0;
		}
	}

	.footer {
		grid-row: 3;
		box-sizing: border-box;

		@media ${desktop} {
			grid-row: 4;
		}

		& ::slotted(*) {
			padding: ${vars(tokens.spacing_lg)} ${vars(tokens.spacing_lg)} ${vars(tokens.spacing_2xl)};
		}
	}

	/* Big text / zoomed-in: everything scrolls together */
	@container (max-height: 20rem) {
		.inner {
			grid-template-rows: auto auto auto auto;
			overflow: auto;
		}

		.header-wrapper {
			grid-row: 2;
			justify-content: flex-start;
			padding-inline: ${vars(tokens.spacing_lg)};
			padding-block-start: 0;
			align-items: start;
			border-block-end-color: transparent;
		}

		.header-wrapper::before {
			content: unset;
		}

		.header-actions {
			grid-row: 1;
			position: sticky;
			top: 0;
			background-color: ${vars(locals.background_color)};
		}

		.body {
			grid-row: 3;
			overflow: visible;
			min-height: unset;
		}

		.footer {
			grid-row: 4;
		}
	}

	/* When resizable, keep the drag handle visible even at small container heights */
	:host([resizable]) .header-wrapper::before {
		content: '';

		@media (hover: hover), ${desktop} {
			content: unset;
		}
	}

	.observable {
		width: 100svw;
		position: fixed;
		top: 200vw;
		left: 200vw;
		visibility: hidden;
	}
`
